import { defineRecipe } from '@chakra-ui/react'

export const assiduousButtonRecipe = defineRecipe({
  className: 'assiduous-button',
  base: {
    alignItems: 'center',
    appearance: 'none',
    borderRadius: '5px',
    borderStyle: 'solid',
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'inline-flex',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    transitionDuration: 'fast',
    transitionProperty: 'background, border-color, color, box-shadow',
    '& [data-button-icon]': {
      alignItems: 'center',
      display: 'inline-flex',
      flexShrink: 0,
      justifyContent: 'center',
      lineHeight: 1,
    },
    '& [data-button-icon] > svg': {
      display: 'block',
      height: '100%',
      width: '100%',
    },
    _focusVisible: {
      outline: '2px solid',
      outlineColor: 'focus.ring',
      outlineOffset: '2px',
    },
    _disabled: {
      cursor: 'not-allowed',
      opacity: 0.45,
    },
  },
  variants: {
    variant: {
      primaryBlue: {
        bg: 'action.primary.bg',
        borderColor: 'action.primary.bg',
        color: 'action.primary.fg',
        _hover: {
          bg: 'action.primary.hover',
          borderColor: 'action.primary.hover',
        },
        _active: {
          bg: 'action.primary.active',
          borderColor: 'action.primary.active',
        },
      },
      primaryBlack: {
        bg: 'action.strong.bg',
        borderColor: 'action.strong.bg',
        color: 'action.strong.fg',
        _hover: {
          bg: 'action.strong.hover',
          borderColor: 'action.strong.hover',
        },
        _active: {
          bg: 'action.strong.active',
          borderColor: 'action.strong.active',
        },
      },
      secondaryBlue: {
        bg: 'surface.panel',
        borderColor: 'action.primary.bg',
        color: 'action.primary.bg',
        _hover: {
          bg: 'action.primary.subtle',
        },
        _active: {
          bg: 'assiduous.blue.100',
        },
      },
      secondaryBlack: {
        bg: 'surface.panel',
        borderColor: 'action.strong.bg',
        color: 'action.strong.bg',
        _hover: {
          bg: 'surface.subtle',
        },
        _active: {
          bg: 'assiduous.neutral.200',
        },
      },
      supporting: {
        bg: 'surface.panel',
        borderColor: 'action.supporting.border',
        color: 'action.supporting.fg',
        _hover: {
          bg: 'surface.subtle',
        },
        _active: {
          bg: 'assiduous.neutral.200',
        },
      },
      destructive: {
        bg: 'action.danger.bg',
        borderColor: 'action.danger.bg',
        color: 'action.danger.fg',
        _hover: {
          bg: 'action.danger.hover',
          borderColor: 'action.danger.hover',
        },
        _active: {
          bg: 'action.danger.active',
          borderColor: 'action.danger.active',
        },
      },
      quiet: {
        bg: 'transparent',
        borderColor: 'transparent',
        color: 'text.primary',
        _hover: {
          bg: 'surface.subtle',
        },
        _active: {
          bg: 'action.primary.subtle',
        },
      },
      navigation: {
        bg: 'surface.panel',
        borderColor: 'action.supporting.border',
        color: 'action.supporting.fg',
        _hover: {
          bg: 'surface.subtle',
        },
        _active: {
          bg: 'assiduous.neutral.200',
        },
      },
      actionCard: {
        bg: 'surface.panel',
        borderColor: 'border.default',
        color: 'text.primary',
        justifyContent: 'flex-start',
        _hover: {
          bg: 'surface.subtle',
          borderColor: 'border.strong',
        },
        _active: {
          bg: 'action.primary.subtle',
          borderColor: 'action.primary.bg',
        },
        _pressed: {
          bg: 'action.primary.subtle',
          borderColor: 'action.primary.bg',
        },
        '& [data-button-icon]': {
          bg: 'action.primary.bg',
          borderRadius: 'full',
          color: 'action.primary.fg',
        },
      },
    },
    size: {
      standalone: {
        borderWidth: '1.5px',
        gap: '8px',
        h: '48px',
        minW: '160px',
        px: '28px',
        textStyle: 'buttonStandalone',
        '& [data-button-icon]': { boxSize: '16px' },
        _icon: { boxSize: '16px' },
      },
      compact: {
        borderWidth: '1px',
        gap: '6px',
        h: '32px',
        minW: '110px',
        px: '14px',
        textStyle: 'buttonCompact',
        '& [data-button-icon]': { boxSize: '14px' },
        _icon: { boxSize: '14px' },
      },
      icon: {
        borderWidth: '1px',
        gap: 0,
        h: '32px',
        minW: '32px',
        p: 0,
        w: '32px',
        '& [data-button-icon]': { boxSize: '16px' },
        _icon: { boxSize: '16px' },
      },
      actionCard: {
        borderWidth: '1px',
        gap: '12px',
        h: '76px',
        minW: 0,
        px: '16px',
        textStyle: 'headingSm',
        w: 'full',
        '& [data-button-icon]': { boxSize: '42px' },
        '& [data-button-icon] > svg': {
          height: '18px',
          width: '18px',
        },
      },
    },
    elevated: {
      true: { boxShadow: 'assiduousButtonRaised' },
      false: { boxShadow: 'none' },
    },
  },
  defaultVariants: {
    elevated: false,
    size: 'standalone',
    variant: 'primaryBlue',
  },
})
